import { Runtype, Static } from "runtypes";
import { z } from "zod";

// TODO If we update to use structured outputs, we can remove this
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Coercion function needs to accept any type
export const coerceToOptional = (v: any): any => {
  // Coerce all agent-generated nullish values to `undefined`
  if (
    v == null ||
    v === "" ||
    v === "null" ||
    v === "undefined" ||
    v === "None"
  ) {
    return undefined;
  }
  return v;
};

/**
 * Wrap a runtype so it can be embedded in a zod schema. Validation still runs
 * through the runtype, so there is no second copy of the schema to keep in
 * sync; the value is returned untouched on success.
 */
export function zFromRuntype<R extends Runtype>(
  runtype: R,
): z.ZodType<Static<R>, z.ZodTypeDef, Static<R>> {
  return z.custom<Static<R>>((value) => runtype.guard(value));
}

/**
 * `z.record` keyed by a branded string. Plain `z.record` can't tell that a
 * branded key is still a string, so it infers `Partial<Record<K, V>>` and makes
 * every value possibly-undefined; this keeps the `Record<K, V>` shape that the
 * hand-written types these schemas replace already had.
 */
export function zBrandedRecord<K extends string, V extends z.ZodTypeAny>(
  value: V,
): z.ZodType<Record<K, z.infer<V>>, z.ZodTypeDef, Record<K, z.input<V>>> {
  return z.record(z.string(), value);
}

/**
 * A value validated only as an object, for the types that are external or
 * code-generated and so have no schema to bridge with `zFromRuntype`. Nothing inside
 * is checked; the declared type is asserted, so callers still get `T` and
 * `ZodShapeFor` can hold a surrounding schema to its interface.
 *
 * Reach for this only where a real schema is out of reach -- it buys a type, not
 * validation. Prefer describing the shape whenever the source is hand-written.
 */
export function zUnvalidatedObject<T>(): z.ZodType<T, z.ZodTypeDef, unknown> {
  return z.record(z.string(), z.unknown()) as z.ZodType<
    T,
    z.ZodTypeDef,
    unknown
  >;
}

/**
 * {@link zUnvalidatedObject} for a value whose top level is an array. Needed as its
 * own helper because the two are not interchangeable: `z.record` rejects an array and
 * `z.array` rejects an object, so an array-shaped type run through
 * `zUnvalidatedObject` would fail the one check either helper still performs.
 */
export function zUnvalidatedArray<T>(): z.ZodType<T, z.ZodTypeDef, unknown> {
  // `as unknown as` rather than the plain `as` its sibling above uses, which is not an
  // oversight: `ZodRecord`'s output overlaps an arbitrary object type enough for a
  // direct assertion, while `ZodArray<ZodUnknown>` and `ZodType<T>` do not overlap for
  // an arbitrary `T`, so the compiler rejects it.
  return z.array(z.unknown()) as unknown as z.ZodType<T, z.ZodTypeDef, unknown>;
}

/**
 * `z.array` whose inferred type is `readonly T[]`, without freezing at parse
 * time.
 *
 * runtypes' `.asReadonly()` was purely type-level. zod's `.readonly()` calls
 * `Object.freeze` on the parsed value, which would turn a later mutation into a
 * silent no-op or a throw, so it is not a drop-in replacement -- this is.
 */
export function zReadonlyArray<V extends z.ZodTypeAny>(
  value: V,
): z.ZodType<readonly z.infer<V>[], z.ZodTypeDef, readonly z.input<V>[]> {
  return z.array(value);
}

/**
 * `Dictionary`'s own rule: a required value gives the full `Record<K, V>`, an
 * `Optional` one gives `Partial<Record<K, V>>`.
 */
type KeyedRecord<K extends string | number | symbol, V> = undefined extends V
  ? Partial<Record<K, V>>
  : Record<K, V>;

/**
 * The drop-in for `z.record(Key, Value)`. `z.record` widens to
 * `Partial<Record<K, V>>` for any key narrower than `string` -- a brand or a
 * `z.enum` alike -- which makes every value possibly-undefined; the hand-written
 * types these schemas stand in for infer the full `Record<K, V>` when the value is
 * required. Keys are still validated against `key`, so only the inferred type moves.
 */
export function zKeyedRecord<
  K extends z.ZodType<string, z.ZodTypeDef, string>,
  V extends z.ZodTypeAny,
>(
  key: K,
  value: V,
): z.ZodType<
  KeyedRecord<z.infer<K>, z.infer<V>>,
  z.ZodTypeDef,
  KeyedRecord<z.input<K>, z.input<V>>
> {
  return z.record(key, value);
}
